import React from "react";
import { DispatchProp } from "react-redux";
import { intersectionBy, find } from "../../utils/lodash";
import { QuestionModel } from "../../models/QuestionModel";
import { BaseControl, ControlProps } from "./BaseControl";
import { ChoiceModel } from "../../models/ChoiceModel";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getSettingBoolean } from "../../models/utilities/settingUtilities";
import { VariableModel } from "../../models/VariableModel";
import { isNullOrEmpty } from "../../utils";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { hasOpenEnd } from "../../models/utilities/choiceUtilities";
import DrillDownRenderer from "../renderers/v2/DrillDownRenderer";
import { getRendererProps } from "../renderers/BaseRenderer";
import { setVariableMentions } from "../../redux/actions";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";

interface Props extends ControlProps, DispatchProp {}

interface State {
    popupX: number;
    popupY: number;
    openEndValue?: { [key: string]: string };
}

export class DrillDownControl extends BaseControl<Props, State> {
    drillDownRef: HTMLElement;

    popUpRef: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            popupX: 0,
            popupY: 0,
            openEndValue: {},
        };
    }

    initializeQuestion(question: QuestionModel, props: Props): void {
        super.initializeQuestion(question, props);
        this.setDefaultChoices(props);
        this.setState({
            popupX: 0,
            popupY: 0,
        });
        this.updateData(props);
    }

    setDefaultChoices(props: Props): void {
        if (!this.hasMentions(props)) {
            for (const variable of props.sequencedVariables) {
                const defaultChoices = variable.choiceList.filter((a) =>
                    getSettingBoolean(a, "Default"),
                );

                const dropdownMentions = this.getMentions(props, variable.name);

                if (defaultChoices.length > 0 && dropdownMentions.length < 1) {
                    this.setMentions(props, variable.name, (mentions) => {
                        mentions = mentions.clear();
                        for (const choice of defaultChoices) {
                            mentions = mentions.add(choice.value);
                        }
                        return mentions;
                    });
                }
            }
        }
    }

    willUpdateMentions(question: QuestionModel, props: Props) {
        // Validate selected choices in the drilldown, making
        // sure choices have their parent value selected.
        let parentSelected: ChoiceModel[];

        const dropdowns = this.availableDropdowns(props);
        for (let i = 0; i < dropdowns.length; i++) {
            const dropdown = dropdowns[i];
            // Get the selected choices for this dropdown.
            const dropdownMentions = this.getMentions(props, dropdown.name);
            const selectedChoices = intersectionBy(
                dropdown.choiceList,
                dropdownMentions,
                (c) => c.value,
            );
            if (i > 0) {
                // Remove from the list choices not selected in the parent.
                const parentSelectedValues = parentSelected.map((p) => p.value);
                const childrenSelected = selectedChoices.filter(
                    (c) =>
                        isNullOrEmpty(c.parentValue) ||
                        parentSelectedValues.includes(c.parentValue),
                );

                if (selectedChoices.length !== childrenSelected.length) {
                    this.setMentions(props, dropdown.name, (mentions) => {
                        mentions = mentions.clear();
                        childrenSelected.forEach((choice) => {
                            const choiceMention = dropdownMentions.find(
                                (x) => x.value === choice.value,
                            );
                            const openEnd = choiceMention && choiceMention.openEnd;
                            mentions = mentions.add(choice.value, openEnd);
                        });
                        return mentions;
                    });
                }

                parentSelected = childrenSelected;
            } else {
                parentSelected = selectedChoices;
            }
        }
    }

    updateData(props: Props) {
        this.setState((s) => {
            const newState = { ...s };
            this.availableDropdowns(props).forEach((dropdown) => {
                const mention = find(
                    this.getMentions(props, dropdown.name),
                    (m) => !isNullOrEmpty(m.openEnd),
                );
                if (mention !== undefined && !isNullOrEmpty(mention.openEnd)) {
                    newState.openEndValue = {
                        ...newState.openEndValue,
                        [dropdown.name]: mention.openEnd,
                    };
                } else {
                    newState.openEndValue = { ...newState.openEndValue, [dropdown.name]: "" };
                }
            });
            return newState;
        });
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        super.UNSAFE_componentWillReceiveProps(nextProps);
        this.updateData(nextProps);
    }

    getValue = (props: Props, dropdown: VariableModel) => {
        if (dropdown == null) {
            return [];
        }
        return this.getMentions(props, dropdown.name).map((m) => m.value);
    };

    onVariableChanged = (variableName: string, value: string[]) => {
        this.setMentions(this.props, variableName, (builder) =>
            builder.clear().add(value.map((v) => ({ value: v }))),
        );
    };

    availableDropdowns = (props: Props) => props.sequencedVariables.filter((v) => v.choiceList);

    renderQuestion(question: QuestionModel) {
        return (
            <DrillDownRenderer
                {...getRendererProps(this.props)}
                variables={this.availableDropdowns(this.props)}
                onVariableChanged={this.onVariableChanged}
                openEndValue={this.state.openEndValue}
                onOpenEndChange={this.onOpenEndChange}
                onOpenEndBlur={this.onOpenEndBlur}
                getFirstOpenEndedChoice={(variable) => this.getFirstOpenEndedChoice(variable)}
            />
        );
    }

    onOpenEndChange = (variable: VariableModel, choice: ChoiceModel, text: string) => {
        this.setState((state) => {
            const newState = { ...state };
            newState.openEndValue = {
                ...newState.openEndValue,
                [variable.name]: this.truncateOpenEnd(choice, text),
            };
            return newState;
        });
    };

    onOpenEndBlur = (variable: VariableModel, choice: ChoiceModel) => {
        const openEnd = this.state.openEndValue[variable.name];
        this.props.dispatch(
            setVariableMentions(variable.name, (mentions) =>
                mentions.removeValue(choice.value).add(choice.value, openEnd.trim()),
            ),
        );
    };

    getFirstOpenEndedChoice(variable: VariableModel) {
        const value = this.getValue(this.props, variable);
        return variable.choiceList.find((c) => c.settings.OpenEndType && value.includes(c.value));
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const { questionnaire, question, mentions, useClientValidation } = this.props;

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return new ValidationBuilder().build();
        }

        const availableDropdowns = this.availableDropdowns(this.props);
        for (const dropdown of availableDropdowns) {
            const value = this.getValue(this.props, dropdown);
            const min = dropdown.settings.MinRequiredAnswers;
            const max = dropdown.settings.MaxAllowedAnswers;
            if (value.length > 0) {
                if (min !== null && value.length < min) {
                    return this.failValidation(availableDropdowns, "InvalidChoicesMin", {
                        min: String(min),
                    });
                }
                if (max !== null && value.length > max) {
                    return this.failValidation(availableDropdowns, "InvalidChoicesMax", {
                        max: String(max),
                    });
                }
                // Check if selected choice has openEnd.
                const dropdownMentions = mentions[dropdown.name];
                const openEndChoices = dropdown.choiceList.filter(hasOpenEnd);
                for (const choice of openEndChoices) {
                    const mention = dropdownMentions.find((m) => m.value === choice.value);
                    const openEnd = this.state.openEndValue[dropdown.name];
                    if (mention) {
                        const result = this.validateOpenEnd(openEnd, choice, dropdown.name);
                        if (!result.isValid) {
                            return result;
                        }
                    }
                }
            } else if (this.isAnswerRequired(questionnaire, question)) {
                return this.failValidation(availableDropdowns, "IncompleteAnswer");
            }
        }
        return new ValidationBuilder().build();
    }
}
